// progress reports dashboard.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Autocomplete,
} from "@mui/material";
import { fetchAllProgressReportProjects } from "../../Controllers/projectController";
import { useError } from "../../Context/errorContext";
import * as XLSX from "xlsx";
import { formatCurrency, rowType } from "../helpers";
import "../../Styles/tables.css";

const monthMap = {
  0: "January",
  1: "February",
  2: "March",
  3: "April",
  4: "May",
  5: "June",
  6: "July",
  7: "August",
  8: "September",
  9: "October",
  10: "November",
  11: "December",
};

const monthOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const yearOptions = Array.from(
  { length: new Date().getFullYear() - 2025 + 1 },
  (_, i) => 2025 + i,
);

const ProjectReports = () => {
  const [projects, setProjects] = useState([]);
  const [month, setMonth] = useState((new Date().getMonth() - 1 + 12) % 12);
  const [year, setYear] = useState(
    new Date().getFullYear() -
      ((new Date().getMonth() - 1 + 12) % 12 === 11 ? 1 : 0),
  );
  const { setError } = useError();

  const getProgressReportProjects = async () => {
    await fetchAllProgressReportProjects(setProjects, setError);
  };

  useEffect(() => {
    getProgressReportProjects();
  }, []);

  const handleExportClick = () => {
    const rows = projects
      .filter((r) => {
        const lastSubmittedProgress = r.progress.find(
          (p) => new Date(p.start) <= Date.UTC(year, month + 1, 0),
        );
        const startDate = new Date(lastSubmittedProgress?.start);
        const financeYear = month < 6 ? year - 1 : year;
        if (
          startDate < Date.UTC(financeYear, 6, 1) &&
          lastSubmittedProgress?.total_progress === "100.00"
        ) {
          return false;
        }
        return true;
      })
      .map((p) => {
        const totalBudget = Number.parseFloat(
          p.budgetRows.find((r) => r.row_type === rowType.total).value,
        );

        const selectedProgress = p.progress.find(
          (p) =>
            new Date(p.start).getMonth() === month &&
            new Date(p.start).getFullYear() === year,
        );

        const monthProgress = Number.parseFloat(
          selectedProgress?.progress ?? 0,
        );
        const lastSubmittedProgress = p.progress.find(
          (p) => new Date(p.start) <= Date.UTC(year, month + 1, 0),
        );
        const totalProgress = Number.parseFloat(
          selectedProgress?.total_progress ??
            lastSubmittedProgress?.total_progress ??
            0,
        );
        return {
          code: p.code,
          name: (p.acronym ? `${p.acronym}: ` : "") + p.name,
          client: p.client_name,
          manager: p.project_manager_name,
          total_budget: totalBudget,
          [monthMap[month]]: monthProgress,
          [monthMap[month] + " revenue"]:
            Math.round(monthProgress * totalBudget) / 100,
          total_progress: totalProgress,
          total_revenue: Math.round(totalProgress * totalBudget) / 100,
        };
      });
    const fileName =
      new Date().getFullYear() +
      `-${String(month + 1).padStart(2, "0")}-progress.xlsx`;
    const sheetName = `${String(month + 1).padStart(2, "0")}.${year} progress`;
    const wb = XLSX.utils.book_new();

    const ws = Array.isArray(rows?.[0])
      ? XLSX.utils.aoa_to_sheet(rows)
      : XLSX.utils.json_to_sheet(rows);

    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Produces an actual XLSX file download in-browser
    XLSX.writeFile(wb, fileName);
  };

  const columnTemplate = `10% 20% 12% 10% 10% 10% 10% 10% 8%`;
  const renderHeader = () => (
    <Box
      className="table-head"
      display="grid"
      gridTemplateColumns={columnTemplate}
      px={4}
      pl={{ xs: 2, md: 4 }}
      py={1}
      sx={{
        backgroundColor: (t) => t.palette.primary.main, //"#041e41",
        color: (t) => t.palette.text.main,
        fontWeight: "bold",
        wordBreak: "break-word",
      }}
    >
      {[
        "Code",
        "Name",
        "Manager",
        "Total budget",
        monthMap[month] + " progress",
        monthMap[month] + " revenue",
        "Total progress",
        "Total revenue",
        "Actions",
      ].map((label, idx) => (
        <Box
          key={idx}
          display="flex"
          alignItems="center"
          gap={1}
          pr={2}
          flexWrap={"wrap"}
        >
          <Typography variant="body1" fontWeight="bold">
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderRow = (project) => {
    const monthProgress = project.progress.find(
      (p) =>
        new Date(p.start).getMonth() === month &&
        new Date(p.start).getFullYear() === year,
    );
    const lastSubmittedProgress = project.progress.find(
      (p) => new Date(p.start) <= Date.UTC(year, month + 1, 0),
    );
    return (
      <Box
        key={project.uid}
        display="grid"
        gridTemplateColumns={columnTemplate}
        px={4}
        pl={{ xs: 2, md: 4 }}
        py={1}
        sx={{
          alignItems: "flex-start",
          wordBreak: "break-word",
          borderBottom: "1px solid #ddd",
        }}
      >
        <Typography variant="body1" pr={2}>
          {project.code || "-"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.name || "-"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.project_manager_name || "-"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {project.budgetRows.find((r) => r.row_type === rowType.total)
            .value_display + " €"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {monthProgress?.progress ? `${monthProgress?.progress} %` : "N/A"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {monthProgress?.progress
            ? `${formatCurrency((monthProgress.progress * project.budgetRows.find((r) => r.row_type === rowType.total).value) / 100)} €`
            : "N/A"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {lastSubmittedProgress?.total_progress
            ? `${lastSubmittedProgress?.total_progress} %`
            : "N/A"}
        </Typography>
        <Typography variant="body1" pr={2}>
          {lastSubmittedProgress?.total_progress
            ? `${formatCurrency((lastSubmittedProgress?.total_progress * project.budgetRows.find((r) => r.row_type === rowType.total).value) / 100)} €`
            : "N/A"}
        </Typography>
        <Box>
          <Button
            variant="contained"
            href={`/project/${project.uid}`}
            size="small"
          >
            View
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="mainContainer">
      <Stack direction="row" spacing={2}>
        <Autocomplete
          options={monthOptions ?? []}
          getOptionLabel={(option) => monthMap[option]}
          isOptionEqualToValue={(option, value) => option === value}
          value={month}
          onChange={(event, newValue) => setMonth(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Month"
              variant="outlined"
              margin="normal"
              fullWidth
            />
          )}
          sx={{ width: 200 }}
        />
        <Autocomplete
          options={yearOptions ?? []}
          getOptionLabel={(option) => String(option)}
          isOptionEqualToValue={(option, value) => option === value}
          value={year}
          onChange={(event, newValue) => setYear(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Year"
              variant="outlined"
              margin="normal"
              fullWidth
            />
          )}
          sx={{ width: 200 }}
        />
      </Stack>

      <Typography sx={{ p: 1, fontWeight: "bold" }}>
        NB! Monthly and total revenue are automatically calculated based on
        budget and progress reports.
      </Typography>
      {renderHeader()}
      {projects
        ?.filter((r) => {
          const lastSubmittedProgress = r.progress.find(
            (p) => new Date(p.start) <= Date.UTC(year, month + 1, 0),
          );
          const startDate = new Date(lastSubmittedProgress?.start);
          const financeYear = month < 6 ? year - 1 : year;
          if (
            startDate < Date.UTC(financeYear, 6, 1) &&
            lastSubmittedProgress?.total_progress === "100.00"
          ) {
            return false;
          }
          return true;
        })
        ?.map((p) => renderRow(p))}
      <Button
        variant="outlined"
        color="secondary"
        sx={{ my: 2, ml: 2 }}
        onClick={handleExportClick}
      >
        Export Reports
      </Button>
    </Box>
  );
};

export default ProjectReports;
